import z from "zod";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { TransportCircuitSchema } from "src/api/client-api/transport/useTransportCircuits";
import { TransportStageSchema } from "src/api/client-api/transport/transportStages";
import {
    getRemoteCollectionState,
    parseHydraCollectionMembers,
    type RemoteCollectionState,
} from "src/api/remoteCollectionState";

export const IngredientSupplierTransportSchema = z.object({
    "@id": z.string().optional(),
    id: z.number(),
    ingredientSupplier: z.union([z.string(), z.object({ "@id": z.string(), id: z.number() })]),
    stage: TransportStageSchema,
    transportCircuit: z.union([z.string(), TransportCircuitSchema]),
});

export type IngredientSupplierTransport = z.infer<typeof IngredientSupplierTransportSchema>;
export type IngredientSupplierTransportsState = RemoteCollectionState<IngredientSupplierTransport>;

export type IngredientSupplierTransportWrite = {
    ingredientSupplier: IRI;
    stage: z.infer<typeof TransportStageSchema>;
    transportCircuit: IRI;
};

export const ingredientSupplierTransportsKey = "ingredientSupplierTransports";

export const ingredientSupplierTransportKeys = {
    list: (organizationId?: number, ingredientSupplierId?: number) =>
        [ingredientSupplierTransportsKey, "list", organizationId ?? null, ingredientSupplierId ?? null] as const,
};

export const useIngredientSupplierTransports = (params?: {
    organizationId?: number;
    ingredientSupplierId?: number;
    enabled?: boolean;
}) => {
    const organizationId = params?.organizationId;
    const enabled = (params?.enabled ?? true) && organizationId !== undefined;

    const query = useQuery({
        enabled,
        keepPreviousData: false,
        queryKey: ingredientSupplierTransportKeys.list(organizationId, params?.ingredientSupplierId),
        queryFn: async () => {
            if (!enabled || organizationId === undefined) {
                throw new Error("Cannot load ingredient supplier transports while disabled or without an organization");
            }

            return await loggedInApiCall.get("/api/ingredient-supplier-transports", {
                params: {
                    itemsPerPage: 100,
                    organization_id: organizationId,
                    ...(params?.ingredientSupplierId ? { "ingredientSupplier.id": params.ingredientSupplierId } : {}),
                },
            });
        },
        select: (request) => {
            return parseHydraCollectionMembers(
                request.data,
                IngredientSupplierTransportSchema,
                "useIngredientSupplierTransports",
            );
        },
    });
    const ingredientSupplierTransportsState: IngredientSupplierTransportsState = getRemoteCollectionState({
        data: query.data,
        enabled,
        error: query.error,
        isLoading: query.isLoading,
    });

    return {
        ingredientSupplierTransports: enabled ? query.data : undefined,
        ingredientSupplierTransportsState,
        ...query,
        data: enabled ? query.data : undefined,
    };
};

export const useUpsertIngredientSupplierTransport = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: IngredientSupplierTransportWrite) => {
            const response = await loggedInApiCall.put(
                "/api/ingredient-supplier-transports/by-ingredient-supplier-stage",
                payload,
            );

            try {
                return zodParseWithLog(
                    response.data,
                    IngredientSupplierTransportSchema,
                    "useUpsertIngredientSupplierTransport",
                );
            } finally {
                void queryClient.invalidateQueries([ingredientSupplierTransportsKey]);
            }
        },
    });
};

export const useDeleteIngredientSupplierTransport = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: number) => {
            return await loggedInApiCall.delete(`/api/ingredient-supplier-transports/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries([ingredientSupplierTransportsKey]);
        },
    });
};
